
import { computed, defineComponent, onMounted, reactive, ref, watch } from 'vue'
import { CategoryType, CtCategory, CtPacking } from '@/models/interfaces/content-type'
import { generateUUID } from '@Protium/vue-ozone-utils'
import { MetricLengthUnit } from '@/models/generics'
import { cloneDeep } from 'lodash'
import Directr, { ContentType } from '@/services/directr'
import CloseIcon from '@/components/Icons/CloseIcon.vue'
import DButton from '@/components/base/DButton.vue'
import TickIcon from '@/components/Icons/TickIcon.vue'
import TrashIcon from '@/components/Icons/TrashIcon.vue'
import PenIcon from '@/components/Icons/PenIcon.vue'
import AddIcon from '@/components/Icons/AddIcon.vue'
import { useQuasar } from 'quasar'
import { useStore } from 'vuex'
import EditableBlockDimensionInput from '@/components/EditableBlockDimensionInput.vue'
import { kebabCase } from 'lodash'

export default defineComponent({
  name: 'PackingGroupModal',
  components: {
    AddIcon,
    PenIcon,
    TrashIcon,
    TickIcon,
    DButton,
    CloseIcon,
    EditableBlockDimensionInput,
  },

  props: {
    showCreatePackingGroup: {
      type: Boolean,
      default: false
    },
    categoryId: {
      type: String,
      required: false,
    }
  },

  emits: ['close', 'save'],

  setup (props, { emit }) {
    const $q = useQuasar()
    const $store = useStore()
    const $directr: Directr = $store.getters['Services/Directr']
    const units = ['mm', 'cm', 'dm', 'm']

    const category = ref<CtCategory | null>(null)
    const categoryPackings = ref<CtPacking[]>([])

    const loadCategory = async () => {
      if (!props.categoryId) {
        category.value = null
        categoryPackings.value = []
        return
      }
      category.value = await $directr.get(ContentType.Category, props.categoryId).then(({ items = [] }) => items[0])
      categoryPackings.value = await $directr.get(ContentType.Packing, [{
        where:
        {
          and: [
            { not: [{ categories: null }] },
            { [`'${props.categoryId}'`]: { any: 'categories' } }
          ]
        }
      }], { orm: true }).then(({ items = [] }) => items)
    }

    void loadCategory()
    watch(() => props.categoryId, (newValue) => {
      void loadCategory()
    })

    const categoryDraft: () => CtCategory = () => ({
      name: '',
      label: '',
      type: CategoryType.Packing,
      sys: {
        type: 'category',
        id: generateUUID(),
        draft: true,
      },
    })

    const packingDraft: () => CtPacking & { editing: boolean } = () => ({
      name: '',
      id: '',
      weight: 0,
      sys: {
        type: 'packing',
        id: generateUUID(),
        draft: true,
      },
      dimensions: {
        x: 0,
        y: 0,
        z: 0,
        unit: MetricLengthUnit.Centimeter,
      },
      editing: true,
    })

    const editState = reactive({
      category: category.value ? cloneDeep(category.value) : categoryDraft(),
      categoryPackings: cloneDeep(categoryPackings.value.map(cp => ({ ...cp, editing: false }))) ?? [],
    })

    const allPackings = ref<CtPacking[]>([])
    const allPackingOptions = computed(() => allPackings.value.map(p => ({ label: p.name?.toString(), value: p.sys.id })))
    const filteredPackingOptions = ref(allPackingOptions.value)
    const loadAllPackingValues = async () => {
      const packings = await $directr.get(ContentType.Packing).then(({ items = [] }) => items)
      const packingIds = packings.map(p => p.sys.id)
      allPackings.value = packings.concat(editState.categoryPackings.filter(cp => !packingIds.includes(cp.sys.id)))
    }
    void loadAllPackingValues()

    watch(() => [category.value, categoryPackings.value], (newValue) => {
      editState.category = category.value ? cloneDeep(category.value) : categoryDraft()
      editState.categoryPackings = cloneDeep(categoryPackings.value.map(cp => ({ ...cp, editing: false })))
      void loadAllPackingValues()
    })




    const filterFn = (val, update) => {
      update(() => {
        if (!val?.length) {
          filteredPackingOptions.value = allPackingOptions.value
            .filter(fp => !editState.categoryPackings.some(p => p.sys.id === fp.value))
        } else {
          const needle = val.toLowerCase()
          filteredPackingOptions.value = allPackingOptions.value
            .filter(fp => (fp.label ?? '').toLowerCase().indexOf(needle) > -1)
            .filter(fp => !editState.categoryPackings.some(p => p.sys.id === fp.value))
        }
      })
    }

    const addPackingSize = () => {
      editState.categoryPackings.push(packingDraft())
    }

    const updatePackingGroup = (index, event) => {
      const packing = allPackings.value.find(
        packing => packing.sys.id === event.value
      )

      if (packing) {
        editState.categoryPackings[index] = { dimensions: { x: 0, y: 0, z: 0 }, ...packing, editing: true }
      }
      filteredPackingOptions.value.splice(index, 1)
    }

    const confirmPacking = (index) => {
      if (!editState.categoryPackings[index].name?.length) {
        $q.notify({
          message: 'Please provide a size name',
          position: 'top',
          classes: 'custom-notify error-notify',
        })
        return
      }
      editState.categoryPackings[index].editing = false
    }

    const editPacking = (index) => {
      editState.categoryPackings[index].editing = true
    }

    const removePacking = (index) => {
      editState.categoryPackings.splice(index, 1)
    }

    const savePackingGroup = async () => {
      if (!editState.category.name?.length) {
        $q.notify({
          message: 'Please provide a group name',
          position: 'top',
          classes: 'custom-notify error-notify',
        })
        return
      }

      let categoryUUID = props.categoryId ?? generateUUID()
      const savedCat = await $directr
        .put(ContentType.Category, categoryUUID, { name: editState.category.name, type: CategoryType.Packing })
        .then(({ items = [] }) => items[0])
      categoryUUID = savedCat.sys.id

      await Promise.all(
        editState.categoryPackings
          .map(({ editing, ...cp }) => cp)
          .map(cp => {
            if (!cp.categories?.includes(categoryUUID)) {
              cp.categories = [...cp.categories ?? [], categoryUUID]
            }
            cp.id = kebabCase(cp.name)
            return cp
          })
          .map(async cp => $directr
            .put(ContentType.Packing, cp.sys.id, cp)
            .then(({ items = [] }) => items[0])
          )
      )

      await Promise.all(
        categoryPackings.value
          .filter(cp => !editState.categoryPackings.find(ecp => cp.sys.id === ecp.sys.id))
          .map(async cp => $directr
            .put(ContentType.Packing, cp.sys.id, {
              ...cp,
              id: kebabCase(cp.name),
              categories: (cp.categories ?? []).filter(c => c !== categoryUUID)
            })
            .then(({ items = [] }) => items[0])
          )
      )

      emit('save', savedCat)

      $q.notify({
        message: 'Packaging saved',
        position: 'top',
        classes: 'custom-notify success',
      })
    }

    const createNewPacking = (index, event): void => {
      editState.categoryPackings[index] = {
        ...editState.categoryPackings[index],
        name: event
      }
    }

    onMounted(() => {
      if (!editState.categoryPackings.length) {
        void addPackingSize()
      }
    })

    return {
      units,
      editState,
      category,
      categoryPackings,
      filteredPackingOptions,
      filterFn,
      addPackingSize,
      updatePackingGroup,
      confirmPacking,
      editPacking,
      removePacking,
      savePackingGroup,
      createNewPacking,
    }
  }
})
